import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { Sales, Tasks, BackOffice } from "../lib/api";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  ShieldCheck, Receipt, Hash, Sparkles, AlertTriangle, Clock, ArrowRight,
  ListTodo, CheckCircle2, Calendar, MoreVertical, ChevronRight,
  AlertCircle, AlarmClock,
} from "lucide-react";
import { cn } from "../lib/utils";
import { buildDeliveryDateTime, getTimeRemaining, useTicker } from "../lib/time";
import SlaBadge from "../components/SlaBadge";
import { HoldBadge } from "../components/TaskHoldControl";
import { fmtDate, fmtShortDateTime } from "../lib/auditFormat";
import { getRegistrationStage } from "../lib/registrationStatus";
import ExportButton from "../components/ExportButton";
import { TransitHoldList } from "../components/TransitHoldList";
import { defaultFilters, buildApiParams } from "../lib/filters";

const TYPE_META = {
  INSURANCE: { icon: ShieldCheck, label: "Insurance",      path: "insurance" },
  ROAD_TAX:  { icon: Receipt,     label: "Road Tax",       path: "road-tax" },
  HSRP:      { icon: Hash,        label: "HSRP",           path: "hsrp" },
  VAS_PP:    { icon: Sparkles,    label: "Protection Plus", path: "vas-pp" },
  VAS_AMC:   { icon: Sparkles,    label: "AMC",            path: "vas-amc" },
  VAS_RSA:   { icon: Sparkles,    label: "RSA",            path: "vas-rsa" },
};

const today = () => new Date().toISOString().slice(0, 10);

const STATUS_TABS = [
  { value: "pending",      label: "Pending" },
  { value: "active",       label: "Active" },
  { value: "on_hold",      label: "On Hold" },
  { value: "delayed",      label: "Delayed" },
  { value: "transit_hold", label: "Transit Hold" },
  { value: "completed",    label: "Completed" },
];

const SLA_OPTIONS = [
  { value: "all",      label: "All SLA" },
  { value: "on_track", label: "On Track" },
  { value: "at_risk",  label: "At Risk" },
  { value: "delayed",  label: "Delayed" },
  { value: "paused",   label: "Paused" },
];

const STATUS_OPTIONS = [
  { value: "all",       label: "All Status" },
  { value: "pending",   label: "Pending" },
  { value: "active",    label: "Active" },
  { value: "on_hold",   label: "On Hold" },
  { value: "delayed",   label: "Delayed" },
  { value: "completed", label: "Completed" },
];

// ──────────────── KPI Card ────────────────
function KpiCard({ tone, Icon, label, value, subtext, active, onClick, testid }) {
  const tones = {
    blue:    { bg: "bg-blue-50/70",    ring: "ring-blue-200",    icon: "bg-blue-100 text-blue-700",    activeRing: "ring-blue-400" },
    emerald: { bg: "bg-emerald-50/70", ring: "ring-emerald-200", icon: "bg-emerald-100 text-emerald-700", activeRing: "ring-emerald-400" },
    amber:   { bg: "bg-amber-50/70",   ring: "ring-amber-200",   icon: "bg-amber-100 text-amber-700",   activeRing: "ring-amber-400" },
    rose:    { bg: "bg-rose-50/70",    ring: "ring-rose-200",    icon: "bg-rose-100 text-rose-700",     activeRing: "ring-rose-400" },
  }[tone] || {};
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={cn(
        "group flex items-center gap-3 rounded-xl px-4 py-3 text-left ring-1 transition-all hover:-translate-y-0.5 hover:shadow-md",
        tones.bg, tones.ring,
        active && `ring-2 ${tones.activeRing} shadow-sm`,
      )}
    >
      <span className={cn("flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full", tones.icon)}>
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-600">
          {label}
        </div>
        <div className="mt-0.5 font-display text-3xl font-bold leading-none tracking-tight text-slate-900">
          {value}
        </div>
        <div className="mt-1 text-[11px] text-slate-500">{subtext}</div>
      </div>
      <ChevronRight className="h-4 w-4 text-slate-400 transition-transform group-hover:translate-x-0.5" />
    </button>
  );
}

// ──────────────── Task Category Card (smaller) ────────────────
function TaskCategoryCard({ Icon, label, value, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={cn(
        "group flex items-center gap-2.5 rounded-lg border border-slate-200 bg-white p-2.5 text-left transition-all hover:-translate-y-0.5 hover:shadow-sm",
        active && "border-primary/40 ring-2 ring-primary/30 shadow-sm",
      )}
    >
      <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-700 group-hover:bg-primary/10 group-hover:text-primary">
        <Icon className="h-3.5 w-3.5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">
          {label}
        </div>
        <div className="font-display text-xl font-bold leading-tight tracking-tight text-slate-900">
          {value}
        </div>
      </div>
      <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
    </button>
  );
}

// ──────────────── Timer pill ────────────────
function TimerPill({ deliveryAt }) {
  if (!deliveryAt) return <span className="text-xs text-slate-400">—</span>;
  const t = getTimeRemaining(deliveryAt);
  const tones = {
    success: "bg-emerald-50 text-emerald-800 border-emerald-200",
    warning: "bg-amber-50 text-amber-800 border-amber-200",
    danger: "bg-rose-50 text-rose-800 border-rose-200",
    neutral: "bg-slate-100 text-slate-700 border-slate-200",
  };
  return (
    <span className={cn(
      "inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 font-mono-data text-xs font-semibold",
      tones[t.tone],
    )}>
      <Clock className="h-3 w-3" />
      {t.label}
    </span>
  );
}

// ──────────────── Stage cell (queue row) ────────────────
// Rich stage display that lets Back Office users see RTO/registration progress
// without opening the file. For ROAD_TAX rows we surface the registration
// pipeline (Pending → Submitted → Completed) with a contextual sub-line:
//   • Pending  → "Not submitted to RTO"
//   • Awaiting → "Submitted: 25/05/26 · 11:15 AM"
//   • Complete → "Regn No: OD-15-AB-1234"
// For non-ROAD_TAX task types we fall back to the simple task label so this
// component can stay the only "Stage" cell across the queue (mixed-type view).
function StageCell({ task, sale, meta, delayed }) {
  if (task.type !== "ROAD_TAX") {
    return (
      <div className="leading-tight">
        <div className="truncate text-xs font-semibold text-slate-900">
          {meta?.label || task.type}
        </div>
      </div>
    );
  }
  const { stage, regNo } = getRegistrationStage(sale);
  // SLA breach paints the dot + label red while keeping the sub-line intact
  // so Back Office can tell *which* stage is delayed.
  const dotCls = delayed ? "bg-rose-500" : stage.dot;
  const labelCls = delayed ? "text-rose-700" : "text-slate-900";
  let sub = "";
  if (stage.key === "road_tax_pending") {
    sub = "Not submitted to RTO";
  } else if (stage.key === "awaiting_rto_approval") {
    const at = fmtShortDateTime(sale?.road_tax?.submitted_to_rto_at);
    sub = at ? `Submitted: ${at}` : "Awaiting RTO approval";
  } else if (stage.key === "registration_completed") {
    sub = regNo ? `Regn No: ${regNo}` : "Registration completed";
  }
  return (
    <div className="leading-tight" data-testid={`bo-stage-${task.id}`}>
      <div className="flex items-center gap-1.5">
        <span className={cn("h-1.5 w-1.5 flex-shrink-0 rounded-full", dotCls)} />
        <span className={cn("truncate text-xs font-semibold", labelCls)}>
          {delayed ? `Delayed · ${stage.label}` : stage.label}
        </span>
      </div>
      {sub && (
        <div className="truncate font-mono-data text-[10px] text-slate-500 pl-3">
          {sub}
        </div>
      )}
    </div>
  );
}



// ──────────────── Page ────────────────
export default function BackOfficeQueue() {
  const { activeLocationIds, locations, activeUser, users } = useApp();
  const locParam = useMemo(() => activeLocationIds.join(","), [activeLocationIds]);
  const locMap = Object.fromEntries(locations.map((l) => [l.id, l]));
  const userMap = Object.fromEntries(users.map((u) => [u.id, u]));
  useTicker(60_000);

  const [stats, setStats] = useState({
    pending: 0, insurance: 0, road_tax: 0, hsrp: 0,
    vas_pp: 0, vas_amc: 0, vas_rsa: 0,
    due_today: 0, at_risk: 0, delayed: 0,
  });
  const [rows, setRows] = useState([]);
  const [transitCount, setTransitCount] = useState(0);
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [slaFilter, setSlaFilter] = useState("all");
  const [dueTodayOnly, setDueTodayOnly] = useState(false);
  const [locFilter, setLocFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [assignedToMe, setAssignedToMe] = useState(false);
  const [filters, setFilters] = useState(defaultFilters());

  // Memoised CSV-export params — avoids fresh object on every render.
  const exportParams = useMemo(() => ({
    ...(typeFilter !== "all" ? { type: typeFilter } : {}),
    status: statusFilter === "delayed" ? "pending" : statusFilter,
    location_ids: locFilter === "all" ? locParam : locFilter,
    ...buildApiParams(filters),
  }), [typeFilter, statusFilter, locFilter, locParam, filters]);

  useEffect(() => {
    (async () => {
      setStats(await BackOffice.stats({ location_ids: locParam }));
    })();
  }, [locParam, statusFilter]);

  // Transit-hold count for the tab badge
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const sales = await Sales.list({ transit_only: true, location_ids: locParam });
        if (!cancelled) setTransitCount(sales.length);
      } catch {
        if (!cancelled) setTransitCount(0);
      }
    })();
    return () => { cancelled = true; };
  }, [locParam, statusFilter]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (statusFilter === "transit_hold") { setRows([]); return; }
      const params = {
        status: statusFilter === "delayed" ? "pending" : statusFilter,
        location_ids: locFilter === "all" ? locParam : locFilter,
        with_sla: "true",
        ...buildApiParams(filters),
      };
      if (typeFilter !== "all") params.type = typeFilter;
      let tasks = await Tasks.list(params);
      if (cancelled) return;
      tasks = tasks.filter((t) => Object.keys(TYPE_META).includes(t.type));
      const saleIds = [...new Set(tasks.map((t) => t.sale_id))];
      const sales = saleIds.length ? await Sales.byIds(saleIds) : [];
      if (cancelled) return;
      const saleBy = Object.fromEntries(sales.map((s) => [s.id, s]));
      let merged = tasks.map((t) => ({ task: t, sale: saleBy[t.sale_id] })).filter((x) => x.sale);
      const td = today();
      if (statusFilter === "delayed") {
        merged = merged.filter((x) =>
          x.sale.delivery?.promised_date && x.sale.delivery.promised_date < td);
      }
      if (statusFilter === "on_hold") {
        merged = merged.filter((x) => x.task.sla_paused);
      } else if (statusFilter === "active") {
        merged = merged.filter((x) => !x.task.sla_paused && x.task.status === "pending");
      }
      // SLA dropdown filter
      if (slaFilter !== "all") {
        merged = merged.filter((x) => {
          if (slaFilter === "paused") return !!x.task.sla_paused;
          return x.task.sla?.status === slaFilter && !x.task.sla_paused;
        });
      }
      // Due today chip (from KPI Due Today card)
      if (dueTodayOnly) {
        merged = merged.filter((x) => (x.sale.delivery?.promised_date || "") === td);
      }
      if (assignedToMe && activeUser?.id) {
        merged = merged.filter((x) => x.task.assigned_user_id === activeUser.id);
      }
      if (search) {
        const q = search.toLowerCase();
        merged = merged.filter((x) =>
          (x.sale.sr_number || "").toLowerCase().includes(q) ||
          (x.sale.customer?.name || "").toLowerCase().includes(q) ||
          (x.sale.customer?.phone || "").includes(q) ||
          (x.sale.vehicle?.vin || "").toLowerCase().includes(q),
        );
      }
      merged.sort((a, b) => {
        const aDel = a.task.status === "pending" && (a.sale.delivery?.promised_date || "") &&
          (a.sale.delivery?.promised_date || "") < td;
        const bDel = b.task.status === "pending" && (b.sale.delivery?.promised_date || "") &&
          (b.sale.delivery?.promised_date || "") < td;
        if (aDel !== bDel) return aDel ? -1 : 1;
        return (a.sale.delivery?.promised_date || "").localeCompare(b.sale.delivery?.promised_date || "");
      });
      setRows(merged);
    })();
    return () => { cancelled = true; };
  }, [typeFilter, statusFilter, slaFilter, dueTodayOnly, locFilter, locParam,
      search, assignedToMe, activeUser, filters]);

  const visibleLocs = locations.filter((l) => activeLocationIds.includes(l.id));

  const resetFilters = () => {
    setTypeFilter("all"); setStatusFilter("pending"); setSlaFilter("all");
    setLocFilter("all"); setSearch(""); setAssignedToMe(false);
    setDueTodayOnly(false); setFilters(defaultFilters());
  };

  return (
    <div className="space-y-3" data-testid="back-office-queue-page">
      {/* ─── Header ─── */}
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">
          Back Office Team
        </div>
        <h1 className="mt-0.5 font-display text-2xl font-bold tracking-tight text-slate-900">
          Back Office Workqueue
        </h1>
        <p className="mt-0.5 text-xs text-slate-500">
          Insurance · Road Tax · HSRP · Protection Plus · AMC · RSA — track every pending file and meet delivery promise.
        </p>
      </div>

      {/* ─── Level 1: Main KPI Cards ─── */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard tone="blue"   Icon={ListTodo}   label="Total Pending" value={stats.pending}
          subtext="All pending tasks"
          active={typeFilter === "all" && !dueTodayOnly && slaFilter === "all" && statusFilter !== "delayed"}
          onClick={() => { setTypeFilter("all"); setStatusFilter("pending"); setSlaFilter("all"); setDueTodayOnly(false); }}
          testid="bo-kpi-pending" />
        <KpiCard tone="emerald" Icon={Calendar}  label="Due Today"    value={stats.due_today}
          subtext="Due today"
          active={dueTodayOnly}
          onClick={() => setDueTodayOnly((v) => !v)}
          testid="bo-kpi-due-today" />
        <KpiCard tone="amber"   Icon={AlarmClock} label="SLA Risk"    value={stats.at_risk}
          subtext="At risk of breach"
          active={slaFilter === "at_risk"}
          onClick={() => { setSlaFilter(slaFilter === "at_risk" ? "all" : "at_risk"); setStatusFilter("pending"); }}
          testid="bo-kpi-sla-risk" />
        <KpiCard tone="rose"    Icon={AlertCircle} label="Delayed"   value={stats.delayed}
          subtext="SLA breached"
          active={statusFilter === "delayed"}
          onClick={() => setStatusFilter(statusFilter === "delayed" ? "pending" : "delayed")}
          testid="bo-kpi-delayed" />
      </div>

      {/* ─── Level 2: Task Category Cards ─── */}
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">
          Task Category
        </div>
        <div className="mt-2 grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-6">
          <TaskCategoryCard Icon={ShieldCheck} label="Insurance"      value={stats.insurance}
            active={typeFilter === "INSURANCE"}
            onClick={() => setTypeFilter(typeFilter === "INSURANCE" ? "all" : "INSURANCE")}
            testid="bo-cat-insurance" />
          <TaskCategoryCard Icon={Receipt}     label="Road Tax"       value={stats.road_tax}
            active={typeFilter === "ROAD_TAX"}
            onClick={() => setTypeFilter(typeFilter === "ROAD_TAX" ? "all" : "ROAD_TAX")}
            testid="bo-cat-roadtax" />
          <TaskCategoryCard Icon={Hash}        label="HSRP"           value={stats.hsrp}
            active={typeFilter === "HSRP"}
            onClick={() => setTypeFilter(typeFilter === "HSRP" ? "all" : "HSRP")}
            testid="bo-cat-hsrp" />
          <TaskCategoryCard Icon={Sparkles}    label="Protection Plus" value={stats.vas_pp}
            active={typeFilter === "VAS_PP"}
            onClick={() => setTypeFilter(typeFilter === "VAS_PP" ? "all" : "VAS_PP")}
            testid="bo-cat-pp" />
          <TaskCategoryCard Icon={Sparkles}    label="AMC"            value={stats.vas_amc}
            active={typeFilter === "VAS_AMC"}
            onClick={() => setTypeFilter(typeFilter === "VAS_AMC" ? "all" : "VAS_AMC")}
            testid="bo-cat-amc" />
          <TaskCategoryCard Icon={Sparkles}    label="RSA"            value={stats.vas_rsa}
            active={typeFilter === "VAS_RSA"}
            onClick={() => setTypeFilter(typeFilter === "VAS_RSA" ? "all" : "VAS_RSA")}
            testid="bo-cat-rsa" />
        </div>
      </div>

      {/* ─── Unified filter toolbar — single row, text-only controls, ≤56px ─── */}
      <Card className="border-slate-200 px-3 py-2">
        <div className="flex flex-wrap items-center gap-2">
          <Input
            placeholder="Search SR / Customer / Mobile / VIN / Regn / Ledger"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 min-w-[260px] flex-[3]"
            data-testid="bo-search"
          />
          <ToolbarSelect value={locFilter} onChange={setLocFilter}
            placeholder="All Locations"
            options={[
              { value: "all", label: "All Locations" },
              ...visibleLocs.map((l) => ({ value: l.id, label: `${l.code} — ${l.name}` })),
            ]}
            testid="bo-loc-filter" />
          <ToolbarSelect value={typeFilter} onChange={setTypeFilter}
            placeholder="All Task Types"
            options={[
              { value: "all", label: "All Task Types" },
              ...Object.entries(TYPE_META).map(([k, m]) => ({ value: k, label: m.label })),
            ]}
            testid="bo-type-filter" />
          <ToolbarSelect value={statusFilter} onChange={setStatusFilter}
            placeholder="Any Status"
            options={STATUS_OPTIONS}
            testid="bo-status-filter" />
          <ToolbarSelect value={slaFilter} onChange={setSlaFilter}
            placeholder="Any SLA"
            options={SLA_OPTIONS}
            testid="bo-sla-filter" />
          <label className="inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 whitespace-nowrap">
            <input type="checkbox" checked={assignedToMe}
              onChange={(e) => setAssignedToMe(e.target.checked)}
              data-testid="bo-assigned-to-me" />
            Assigned to me
          </label>
          <button
            type="button"
            onClick={resetFilters}
            className="inline-flex h-9 items-center rounded-md border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600 hover:bg-slate-50"
            data-testid="bo-reset"
          >
            Reset
          </button>
          <div className="ml-auto flex items-center gap-2">
            <ExportButton
              endpoint="registration"
              params={exportParams}
              prefix="Registration_Tracker"
              label="Download Registration Excel"
              testid="registration-export-xlsx"
            />
            <ExportButton
              endpoint="tasks"
              params={exportParams}
              prefix={typeFilter !== "all" ? `BackOffice_${typeFilter}` : "BackOffice"}
            />
          </div>
        </div>
      </Card>

      {/* ─── Status tabs (underline) ─── */}
      <div className="border-b border-slate-200">
        <div className="flex flex-wrap items-center gap-1">
          {STATUS_TABS.map((t) => {
            const isActive = statusFilter === t.value;
            const showCount = t.value === "transit_hold" && transitCount > 0;
            return (
              <button
                key={t.value}
                type="button"
                onClick={() => setStatusFilter(t.value)}
                className={cn(
                  "relative -mb-px px-3.5 py-2 text-sm font-semibold transition-colors",
                  isActive ? "text-blue-600" : "text-slate-500 hover:text-slate-800",
                )}
                data-testid={`bo-tab-${t.value}`}
              >
                {t.label}{showCount ? ` (${transitCount})` : ""}
                {isActive && (
                  <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-blue-600" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {statusFilter === "transit_hold" ? (
        <TransitHoldList
          locParam={locFilter === "all" ? locParam : locFilter}
          locMap={locMap}
          userMap={userMap}
          canRelease={false}
          testidPrefix="bo"
          onCountChange={setTransitCount}
        />
      ) : (
      /* ─── Compact table — no horizontal scroll, fits in viewport ─── */
      <Card className="border-slate-200 overflow-hidden">
        {rows.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-slate-500">No tasks found.</div>
        ) : (
          <div>
            <table className="w-full table-fixed divide-y divide-border text-sm">
              <colgroup>
                <col style={{ width: "20%" }} />
                <col style={{ width: "11%" }} />
                <col style={{ width: "16%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "9%" }} />
                <col style={{ width: "8%" }} />
                <col style={{ width: "10%" }} />
              </colgroup>
              <thead className="bg-slate-50">
                <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  <th className="px-3 py-2">Customer</th>
                  <th className="px-3 py-2">Vehicle / Location</th>
                  <th className="px-3 py-2">Stage</th>
                  <th className="px-3 py-2">SLA / Status</th>
                  <th className="px-3 py-2">DSE</th>
                  <th className="px-3 py-2">Delivery</th>
                  <th className="px-3 py-2">Left</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-white">
                {rows.map(({ task, sale }) => {
                  const meta = TYPE_META[task.type];
                  const td = today();
                  const delayed = task.status === "pending" &&
                    sale.delivery?.promised_date && sale.delivery.promised_date < td &&
                    !sale.transit_hold?.active;
                  const deliveryAt = buildDeliveryDateTime(sale.delivery?.promised_date, sale.delivery?.promised_time);
                  const dse = userMap[sale.dse_id];
                  const loc = locMap[sale.location_id];
                  return (
                    <tr key={task.id}
                      className={cn(
                        "h-[62px] transition-colors hover:bg-slate-50",
                        delayed && "bg-rose-50/40",
                      )}
                      data-testid={`bo-row-${task.id}`}
                    >
                      {/* Customer + SR (merged) */}
                      <td className="px-3 py-1.5">
                        <div className="truncate text-sm font-semibold text-slate-900 leading-tight">
                          {sale.customer?.name || "—"}
                        </div>
                        <div className="truncate font-mono-data text-[11px] text-slate-500 leading-tight">
                          {sale.customer?.phone || "—"}
                        </div>
                        <div className="truncate font-mono-data text-[10px] font-bold text-slate-700 leading-tight">
                          {sale.sr_number || "—"}
                        </div>
                      </td>
                      <td className="px-3 py-1.5">
                        <div className="truncate text-sm text-slate-900 leading-tight">
                          {sale.vehicle?.model || "—"}
                        </div>
                        <div className="truncate font-mono-data text-[11px] text-slate-500 leading-tight">
                          {loc ? `${loc.name} (${loc.code})` : "—"}
                        </div>
                      </td>
                      <td className="px-3 py-1.5">
                        <StageCell task={task} sale={sale} meta={meta} delayed={delayed} />
                      </td>
                      <td className="px-3 py-1.5">
                        <div className="flex flex-wrap items-center gap-1">
                          <StatusPill status={task.status} delayed={delayed} />
                          <SlaBadge task={task} />
                          <HoldBadge task={task} />
                        </div>
                      </td>
                      <td className="px-3 py-1.5">
                        <span className="truncate text-xs font-medium text-slate-800"
                              data-testid={`bo-dse-${task.id}`}>
                          {dse?.name || "—"}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 font-mono-data text-[11px] text-slate-700">
                        {fmtDate(sale.delivery?.promised_date) || "—"}
                      </td>
                      <td className="px-3 py-1.5">
                        <TimerPill deliveryAt={deliveryAt} />
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        <Link
                          to={`/back-office/${meta?.path}/${sale.id}`}
                          className="inline-flex items-center gap-0.5 rounded-md bg-primary/5 px-2 py-1 text-[11px] font-semibold text-primary hover:bg-primary/10"
                          data-testid={`bo-open-${task.id}`}
                        >
                          {task.status === "completed" ? "View" : "Update"}
                          <ArrowRight className="h-3 w-3" />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      )}
    </div>
  );
}

// ──────────────── Helpers ────────────────
function ToolbarSelect({ value, onChange, placeholder, options, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 w-[150px] text-xs" data-testid={testid}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function StatusPill({ status, delayed }) {
  if (status === "completed") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">
        <CheckCircle2 className="h-3 w-3" /> Completed
      </span>
    );
  }
  if (status === "skipped") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-semibold text-slate-600">
        Skipped
      </span>
    );
  }
  if (delayed) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-xs font-semibold text-rose-700">
        <AlertTriangle className="h-3 w-3" /> Delayed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800">
      <span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> Pending
    </span>
  );
}
